'use client';

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/useToast';
import { usePurchaseOrders } from '@/hooks/usePurchaseOrders';
import { PurchaseOrder } from '@/lib/schemas';
import { cn, formatCurrency } from '@/lib/utils';
import { api } from '@/lib/api';
import {
  ArrowLeft,
  Package,
  Search,
  AlertTriangle,
  CheckCircle,
  Calendar,
} from 'lucide-react';

const statusConfig = {
  draft: { variant: 'default' as const, label: 'Draft' },
  ordered: { variant: 'info' as const, label: 'Ordered' },
  partial: { variant: 'warning' as const, label: 'Partial' },
  received: { variant: 'success' as const, label: 'Received' },
  cancelled: { variant: 'error' as const, label: 'Cancelled' },
};

export default function ReceiveGoodsPage() {
  const navigate = useNavigate();
  const { toast, success, error } = useToast();
  const { data: purchaseOrdersResponse, isLoading: poLoading } = usePurchaseOrders();
  const purchaseOrders = purchaseOrdersResponse?.data || [];

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPOId, setSelectedPOId] = useState<string>('');
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [receivedQuantities, setReceivedQuantities] = useState<Record<string, number>>({});
  const [batchNumbers, setBatchNumbers] = useState<Record<string, string>>({});
  const [expiryDates, setExpiryDates] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter POs: non-cancelled and with items to receive
  const eligiblePOs = useMemo(() => {
    return purchaseOrders.filter((po) => {
      const isCancelled = po.status === 'cancelled';
      const hasItemsToReceive = po.items.some(
        (item) => (item.quantity_ordered || 0) > (item.quantity_received || 0)
      );
      return !isCancelled && hasItemsToReceive;
    });
  }, [purchaseOrders]);

  // Filter by search query
  const filteredPOs = useMemo(() => {
    if (!searchQuery) return eligiblePOs;
    const query = searchQuery.toLowerCase();
    return eligiblePOs.filter(
      (po) =>
        (po.po_number || '').toLowerCase().includes(query) ||
        po.vendor_id.toLowerCase().includes(query)
    );
  }, [eligiblePOs, searchQuery]);

  // Load selected PO data
  useEffect(() => {
    if (selectedPOId) {
      const po = purchaseOrders.find((p) => p.po_id === selectedPOId);
      setSelectedPO(po || null);

      // Initialize received quantities from PO data
      if (po) {
        const initialReceived: Record<string, number> = {};
        po.items.forEach((item) => {
          initialReceived[item.item_id] = item.quantity_received || 0;
        });
        setReceivedQuantities(initialReceived);
        setBatchNumbers({});
        setExpiryDates({});
      }
    } else {
      setSelectedPO(null);
      setReceivedQuantities({});
      setBatchNumbers({});
      setExpiryDates({});
    }
  }, [selectedPOId, purchaseOrders]);

  const getVendorName = (vendorId: string) => {
    // In a real app, we'd look up vendor from vendors list
    // For now, return the vendor_id or a placeholder
    return vendorId;
  };

  const handleQuantityChange = (itemId: string, value: number) => {
    if (!selectedPO) return;
    const item = selectedPO.items.find((i) => i.item_id === itemId);
    const prevReceived = receivedQuantities[itemId] || 0;
    const orderedQty = item?.quantity_ordered || 0;
    const maxQty = orderedQty;

    setReceivedQuantities((prev) => ({
      ...prev,
      [itemId]: Math.min(Math.max(0, value), maxQty),
    }));
  };

  const getTotalToReceive = () => {
    if (!selectedPO) return 0;
    return selectedPO.items.reduce((sum, item) => {
      const ordered = item.quantity_ordered || 0;
      const prevReceived = item.quantity_received || 0;
      const currentReceived = receivedQuantities[item.item_id] || prevReceived;
      return sum + (currentReceived - prevReceived);
    }, 0);
  };

  const getTotalValue = () => {
    if (!selectedPO) return 0;
    return selectedPO.items.reduce((sum, item) => {
      const ordered = item.quantity_ordered || 0;
      const prevReceived = item.quantity_received || 0;
      const currentReceived = receivedQuantities[item.item_id] || prevReceived;
      const toReceiveNow = currentReceived - prevReceived;
      return sum + toReceiveNow * (item.unit_price || 0);
    }, 0);
  };

  const getAllItemsValid = () => {
    if (!selectedPO) return false;
    return selectedPO.items.every((item) => {
      const ordered = item.quantity_ordered || 0;
      const prevReceived = item.quantity_received || 0;
      const currentReceived = receivedQuantities[item.item_id] || prevReceived;
      const toReceiveNow = currentReceived - prevReceived;
      return toReceiveNow >= 0 && toReceiveNow <= (ordered - prevReceived);
    });
  };

  const handleCompleteReceipt = async () => {
    if (!selectedPO || !getAllItemsValid() || getTotalToReceive() <= 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const receiveItems = selectedPO.items.map((item) => {
        const prevReceived = item.quantity_received || 0;
        const currentReceived = receivedQuantities[item.item_id] || prevReceived;
        const quantity_received = currentReceived - prevReceived;

        // Only include items that have quantity to receive
        if (quantity_received <= 0) {
          return null;
        }

        const poItemId = item.item_id; // Using item_id as po_item_id (matches schema)

        // Check if traceability requires batch/expiry info
        const needsBatch = ['batch', 'both'].includes(selectedPO.traceability_level);
        const needsExpiry = ['expiry', 'both'].includes(selectedPO.traceability_level);

        return {
          po_item_id: poItemId,
          quantity_received: quantity_received,
          batch_number: needsBatch ? (batchNumbers[item.item_id] || undefined) : undefined,
          expiry_date: needsExpiry ? (expiryDates[item.item_id] || undefined) : undefined,
        };
      }).filter(Boolean);

      await api.receivePurchaseOrder(selectedPO.po_id!, { items: receiveItems as any[] });

      success('Purchase order received', 'The purchase order has been marked as received.');
      navigate('/purchase-orders');
    } catch (err) {
      error('Failed to receive purchase order', 'An error occurred while receiving the goods.');
      console.error('Receive error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Receive Goods
          </h1>
          <p className="text-text-secondary mt-1">
            Receive items from a purchase order
          </p>
        </div>
        <Button variant="outline" onClick={handleCancel}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Purchase Orders
        </Button>
      </div>

      {/* PO Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Select Purchase Order</CardTitle>
          <CardDescription>
            Choose a purchase order to receive goods for
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
              <Input
                placeholder="Search by PO number or vendor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {poLoading ? (
              <div className="text-center py-8 text-text-muted">Loading purchase orders...</div>
            ) : filteredPOs.length === 0 ? (
              <div className="text-center py-8 text-text-muted">
                No eligible purchase orders found
              </div>
            ) : (
              <div className="grid gap-2">
                {filteredPOs.map((po) => {
                  const poStatus = statusConfig[po.status as keyof typeof statusConfig];
                  const itemsToReceive = po.items.reduce(
                    (sum, item) => sum + Math.max(0, (item.quantity_ordered || 0) - (item.quantity_received || 0)),
                    0
                  );
                  const poTotal = po.items.reduce(
                    (sum, item) => sum + (item.line_total || item.quantity_ordered * item.unit_price),
                    0
                  );

                  return (
                    <div
                      key={po.po_id}
                      className={cn(
                        'p-4 border rounded-lg cursor-pointer transition-all',
                        selectedPOId === po.po_id
                          ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      )}
                      onClick={() => setSelectedPOId(po.po_id!)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-text-primary">
                              {po.po_number || 'N/A'}
                            </span>
                            <Badge variant={poStatus.variant}>{poStatus.label}</Badge>
                          </div>
                          <p className="text-sm text-text-secondary">
                            Vendor: {getVendorName(po.vendor_id)}
                          </p>
                          <p className="text-sm text-text-muted">
                            Ordered: {po.items.reduce((sum, i) => sum + i.quantity_ordered, 0)} items
                          </p>
                          <p className="text-sm font-medium text-blue-600">
                            {itemsToReceive} item(s) to receive
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-semibold text-text-primary">
                            {formatCurrency(poTotal)}
                          </p>
                          <p className="text-xs text-text-muted">
                            {po.delivery_date && (
                              <span className="flex items-center justify-end gap-1 mt-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(po.delivery_date).toLocaleDateString()}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Receiving Details - only show when PO is selected */}
      {selectedPO && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Receiving Details</CardTitle>
                <CardDescription>
                  Enter quantities to receive for each item
                </CardDescription>
              </div>
              <Badge variant={selectedPO.traceability_level === 'none' ? 'default' : 'info'}>
                Traceability: {selectedPO.traceability_level}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Total Ordered</p>
                  <p className="text-2xl font-bold text-text-primary">
                    {selectedPO.items.reduce((sum, i) => sum + i.quantity_ordered, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">To Receive Now</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {getTotalToReceive()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Receipt Value</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(getTotalValue())}
                  </p>
                </div>
              </div>

              {/* Items table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-text-secondary">
                        Item
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-text-secondary">
                        Ordered
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-text-secondary">
                       Prev Received
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-text-secondary">
                        Quantity to Receive
                      </th>
                      {selectedPO.traceability_level !== 'none' && (
                        <>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-text-secondary">
                            Batch #
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-text-secondary">
                            Expiry Date
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {selectedPO.items.map((item) => {
                      const orderedQty = item.quantity_ordered || 0;
                      const prevReceived = item.quantity_received || 0;
                      const currentReceived = receivedQuantities[item.item_id] || prevReceived;
                      const remaining = orderedQty - prevReceived;
                      const toReceiveNow = currentReceived - prevReceived;
                      const isFullyReceived = currentReceived >= orderedQty;
                      const isValidQty = toReceiveNow >= 0 && toReceiveNow <= remaining;

                      const needsBatch = ['batch', 'both'].includes(selectedPO.traceability_level);
                      const needsExpiry = ['expiry', 'both'].includes(selectedPO.traceability_level);

                      return (
                        <tr key={item.item_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-text-primary">
                                {item.item_name || `Item ${item.item_id}`}
                              </p>
                              <p className="text-xs text-text-muted">
                                {item.item_code || ''}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-text-primary">{orderedQty}</span>
                            <p className="text-xs text-text-muted">
                              @ {formatCurrency(item.unit_price)}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-text-secondary">{prevReceived}</span>
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              type="number"
                              min={0}
                              max={remaining}
                              value={toReceiveNow}
                              onChange={(e) =>
                                handleQuantityChange(item.item_id, prevReceived + parseInt(e.target.value) || 0)
                              }
                              disabled={remaining === 0}
                              className={cn(
                                'max-w-[100px] mx-auto',
                                !isValidQty && toReceiveNow > 0
                                  ? 'border-red-500 focus-visible:ring-red-500'
                                  : ''
                              )}
                            />
                            {!isValidQty && toReceiveNow > 0 && (
                              <p className="text-xs text-red-600 mt-1 text-center">
                                Max: {remaining}
                              </p>
                            )}
                            {remaining === 0 && (
                              <p className="text-xs text-green-600 mt-1 text-center">
                                Fully received
                              </p>
                            )}
                          </td>
                          {selectedPO.traceability_level !== 'none' && (
                            <>
                              <td className="px-4 py-3">
                                {needsBatch && (
                                  <Input
                                    value={batchNumbers[item.item_id] || ''}
                                    onChange={(e) =>
                                      setBatchNumbers((prev) => ({
                                        ...prev,
                                        [item.item_id]: e.target.value,
                                      }))
                                    }
                                    placeholder="Batch #"
                                    className="max-w-[120px]"
                                  />
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {needsExpiry && (
                                  <Input
                                    type="date"
                                    value={expiryDates[item.item_id] || ''}
                                    onChange={(e) =>
                                      setExpiryDates((prev) => ({
                                        ...prev,
                                        [item.item_id]: e.target.value,
                                      }))
                                    }
                                    className="max-w-[140px]"
                                  />
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Validation warning */}
              {!getAllItemsValid() && (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-semibold">Invalid quantities</p>
                    <p>
                      Some items have quantities exceeding the remaining amount.
                      Please adjust the values.
                    </p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between border-t pt-4">
                <div>
                  {getTotalToReceive() > 0 && getAllItemsValid() && (
                    <Badge variant="success" className="gap-1">
                      <CheckCircle className="h-4 w-4" />
                      Ready to receive {getTotalToReceive()} item(s)
                    </Badge>
                  )}
                  {getTotalToReceive() === 0 && (
                    <Badge variant="default" className="gap-1">
                      Nothing to receive
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCompleteReceipt}
                    disabled={!getAllItemsValid() || getTotalToReceive() <= 0 || isSubmitting}
                  >
                    {isSubmitting ? 'Processing...' : 'Complete Receipt'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state when no PO selected */}
      {!selectedPO && (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">Select a purchase order to start receiving</p>
            <p className="text-sm text-text-muted mt-1">
              Only non-cancelled POs with pending items are shown
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
